import { colors } from 'ms-styles/colors';

type Props = {
  width?: number | undefined;
  height?: number | undefined;
  reversed?: boolean | undefined;
  hasText?: boolean | undefined;
  textColorProp?: string | undefined;
};

const VIEWBOX_WIDTH = 32;
const VIEWBOX_HEIGHT = 25.5;
const VIEWBOX_TEXT_WIDTH = 166;
const VIEWBOX_TEXT_HEIGHT = 27;

const DEFAULT_LOGO_WIDTH = 29;
const DEFAULT_TEXT_LOGO_WIDTH = 146;
const DEFAULT_TEXT_COLOR = colors.black;
const REVERSED_TEXT_COLOR = colors.white;

const getDimensions = ({
  hasText,
  width,
  height,
}: {
  hasText: boolean;
  width?: number | undefined;
  height?: number | undefined;
}): { width: number; height: number } => {
  // If both width and height are provided, use the provided values
  if (width && height) {
    return { width, height };
  }

  const viewboxHeight = hasText ? VIEWBOX_TEXT_HEIGHT : VIEWBOX_HEIGHT;
  const viewboxWidth = hasText ? VIEWBOX_TEXT_WIDTH : VIEWBOX_WIDTH;

  // If either width or height is provided,
  // calculate the dimensions by keeping the ratio the same
  if (width) {
    return {
      width,
      height: (width * viewboxHeight) / viewboxWidth,
    };
  }

  if (height) {
    return {
      width: (height * viewboxWidth) / viewboxHeight,
      height,
    };
  }

  return { width: viewboxWidth, height: viewboxHeight };
};

const Logo = ({
  width,
  height,
  reversed = false,
  hasText = false,
  textColorProp,
}: Props) => {
  const dimensions = getDimensions({
    hasText,
    width:
      width || height
        ? width
        : hasText
        ? DEFAULT_TEXT_LOGO_WIDTH
        : DEFAULT_LOGO_WIDTH,
    height,
  });
  const textColor = textColorProp
    ? textColorProp
    : reversed
    ? REVERSED_TEXT_COLOR
    : DEFAULT_TEXT_COLOR;

  return (
    <svg
      width={dimensions.width}
      height={dimensions.height}
      viewBox={`0 0 ${hasText ? VIEWBOX_TEXT_WIDTH : VIEWBOX_WIDTH} ${
        hasText ? VIEWBOX_TEXT_HEIGHT : VIEWBOX_HEIGHT
      }`}
    >
      {hasText && (
        <path
          d="M58.145 10.178C56.512 10.178 55.454 10.776 54.81 11.696C54.166 10.73 53.154 10.178 51.797 10.178C50.256 10.178 49.244 10.776 48.715 11.558V10.5H45.265V22H48.715V15.514C48.715 14.157 49.359 13.352 50.509 13.352C51.613 13.352 52.165 14.088 52.165 15.238V22H55.615V15.514C55.615 14.157 56.259 13.352 57.409 13.352C58.513 13.352 59.065 14.088 59.065 15.238V22H62.515V14.939C62.515 12.064 60.79 10.178 58.145 10.178ZM73.0224 10.5V11.581C72.2634 10.707 71.1364 10.178 69.5954 10.178C66.5824 10.178 64.0984 12.823 64.0984 16.25C64.0984 19.677 66.5824 22.322 69.5954 22.322C71.1364 22.322 72.2634 21.793 73.0224 20.919V22H76.4724V10.5H73.0224ZM70.2854 19.056C68.6984 19.056 67.5484 17.975 67.5484 16.25C67.5484 14.525 68.6984 13.444 70.2854 13.444C71.8724 13.444 73.0224 14.525 73.0224 16.25C73.0224 17.975 71.8724 19.056 70.2854 19.056ZM85.5425 13.812V10.5H83.1735V7.27999L79.7235 8.31499V10.5H77.8835V13.812H79.7235V17.883C79.7235 21.103 81.0345 22.46 85.5425 22V18.872C84.0245 18.964 83.1735 18.872 83.1735 17.883V13.812H85.5425ZM94.0349 10.178C92.5169 10.178 91.3669 10.73 90.7459 11.581V5.89999H87.2959V22H90.7459V15.721C90.7459 14.088 91.6199 13.352 92.8849 13.352C93.9889 13.352 94.8859 14.019 94.8859 15.445V22H98.3359V14.939C98.3359 11.834 96.3579 10.178 94.0349 10.178ZM103.771 13.858C103.771 13.444 104.139 13.214 104.737 13.214C105.496 13.214 105.956 13.628 106.278 14.226L109.222 12.639C108.256 11.006 106.6 10.178 104.737 10.178C102.345 10.178 100.229 11.443 100.229 13.95C100.229 17.929 105.887 17.262 105.887 18.527C105.887 18.987 105.473 19.24 104.645 19.24C103.633 19.24 102.989 18.757 102.69 17.883L99.7 19.585C100.597 21.448 102.345 22.322 104.645 22.322C107.129 22.322 109.429 21.195 109.429 18.55C109.429 14.295 103.771 15.169 103.771 13.858ZM117.941 10.178C116.4 10.178 115.273 10.707 114.514 11.581V10.5H111.064V26.6H114.514V20.919C115.273 21.793 116.4 22.322 117.941 22.322C120.954 22.322 123.438 19.677 123.438 16.25C123.438 12.823 120.954 10.178 117.941 10.178ZM117.251 19.056C115.664 19.056 114.514 17.975 114.514 16.25C114.514 14.525 115.664 13.444 117.251 13.444C118.838 13.444 119.988 14.525 119.988 16.25C119.988 17.975 118.838 19.056 117.251 19.056ZM133.543 10.5V11.581C132.784 10.707 131.657 10.178 130.116 10.178C127.103 10.178 124.619 12.823 124.619 16.25C124.619 19.677 127.103 22.322 130.116 22.322C131.657 22.322 132.784 21.793 133.543 20.919V22H136.993V10.5H133.543ZM130.806 19.056C129.219 19.056 128.069 17.975 128.069 16.25C128.069 14.525 129.219 13.444 130.806 13.444C132.393 13.444 133.543 14.525 133.543 16.25C133.543 17.975 132.393 19.056 130.806 19.056ZM144.844 22.322C147.098 22.322 149.053 21.149 150.042 19.355L147.029 17.63C146.661 18.435 145.81 18.918 144.798 18.918C143.303 18.918 142.199 17.837 142.199 16.25C142.199 14.663 143.303 13.582 144.798 13.582C145.81 13.582 146.638 14.065 147.029 14.87L150.042 13.122C149.053 11.351 147.075 10.178 144.844 10.178C141.348 10.178 138.749 12.823 138.749 16.25C138.749 19.677 141.348 22.322 144.844 22.322ZM154.088 17.63H162.253C162.345 17.193 162.391 16.733 162.391 16.25C162.391 12.777 159.907 10.178 156.549 10.178C152.915 10.178 150.431 12.823 150.431 16.25C150.431 19.677 152.869 22.322 156.802 22.322C158.987 22.322 160.689 21.517 161.816 19.953L159.056 18.366C158.596 18.872 157.791 19.24 156.848 19.24C155.583 19.24 154.525 18.826 154.088 17.63ZM154.019 15.054C154.341 13.881 155.215 13.237 156.526 13.237C157.561 13.237 158.596 13.72 158.964 15.054H154.019Z"
          fill={textColor}
        />
      )}
      <path
        d="M0 5.88291C0 4.00414 1.87653 2.7037 3.63568 3.36338C3.87368 3.45263 4.08153 3.60748 4.23516 3.80999L12.7377 15.0178C13.8983 16.5477 16.1775 16.6084 17.4179 15.1425L25.6022 5.47007C25.8647 5.15992 26.1874 4.90631 26.5508 4.72462L27.6584 4.17082C29.6531 3.17347 32 4.62396 32 6.8541V21C32 22.6569 30.6569 24 29 24H3C1.34315 24 0 22.6569 0 21V5.88291Z"
        fill="#1ABC9C"
      />
      <path
        d="M16 14.4279C16 13.5245 16.4071 12.6692 17.1082 12.0996L27.1082 3.97457C29.0688 2.38163 32 3.77681 32 6.30291V21C32 22.6569 30.6569 24 29 24H19C17.3431 24 16 22.6569 16 21V14.4279Z"
        fill="#66B1E2"
      />
      <path
        d="M25.281 2.43897C26.8216 0.85565 29.3194 0.85565 30.86 2.43897C32.4005 4.0223 32.4005 6.58937 30.86 8.17269L19.0583 20.3017C17.5177 21.885 15.0199 21.885 13.4793 20.3017C11.9388 18.7184 11.9388 16.1513 13.4793 14.568L25.281 2.43897Z"
        fill="#3F4694"
      />
      <path
        d="M1.1558 8.2142C-0.384778 6.63087 -0.384779 4.0638 1.1558 2.48048C2.69638 0.897152 5.19415 0.897152 6.73473 2.48048L18.755 14.8343C20.2956 16.4176 20.2956 18.9847 18.755 20.568C17.2144 22.1513 14.7166 22.1513 13.1761 20.568L1.1558 8.2142Z"
        fill="#398984"
      />
      <path
        d="M15.9999 12L22.8786 18.8787C24.7685 20.7686 23.43 24 20.7573 24H11.2425C8.56988 24 7.23137 20.7686 9.12126 18.8787L15.9999 12Z"
        fill="#9EDAE2"
      />
      <path
        d="M16 12L21.6569 17.6569L19.5355 19.7782C17.5829 21.7308 14.4171 21.7308 12.4645 19.7782L10.3431 17.6569L16 12Z"
        fill="#36A6B3"
      />
      <path
        d="M-6.00815e-05 5.5L8.45104 6.56768L3.8633 10.998L-0.567017 6.41027L-6.00815e-05 5.5Z"
        fill="#398984"
      />
      <path
        d="M29.5355 2.46447L26 6L29.5355 9.53553L32 7L32 5.5L29.5355 2.46447Z"
        fill="#3F4694"
      />
    </svg>
  );
};

export default Logo;
